import { useEffect, useMemo, useState } from 'react';

import ArtlistIcon from '@/assets/all-icons/artlist-icon';
import config from "@/config";
import useQuery from "@/hooks/useQuery";
import useTranslations from '@/i18n/useTranslations';
import fetchWrapper from '@/services/fetchWrapper.service';
import HomeHeader from '@/shared-components/home/components/HomeHeader';
import Loader from '@/shared-components/loader/Loader';
import StaticSeoHead from '@/shared-components/seo/StaticSeoHead';
import Text from "@/shared-components/text/components/Text";
import AppNavigationPage from '@/shared-components/wrappers/components/AppNavigationPage';
import { getSsrSearchLandingBootstrap } from '@/utils/ssrSearchLandingBootstrap';

import RandomizedHomeLinksList from '../components/RandomizedHomeLinksList';
import useRandomizedHomeLinks from '../hooks/useRandomizedHomeLinks';

import VirtualList from "./components/VirtualList";

import './index.css';

const RANDOM_LIMIT = 24;
const RANDOM_ENDPOINT = `${config.apis.api.url}/home/links/randomized/artlists`;

function readInitialSearchLandingSeoPayload() {
  const payload = getSsrSearchLandingBootstrap('artlists');
  return payload?.seoData && typeof payload.seoData === 'object' ? payload.seoData : null;
}

function HomeArtlistsRandomized() {
  const tProfileTabs = useTranslations('common.profile.navigation.tabs');
  const [searchSeo, setSearchSeo] = useState(() => readInitialSearchLandingSeoPayload());
  const randomizedState = useRandomizedHomeLinks({
    endpoint: RANDOM_ENDPOINT,
    limitParam: 'artlist_limit',
    limit: RANDOM_LIMIT,
    errorMessage: 'Unable to refresh artlists at the moment.',
  });

  useEffect(() => {
    let ignore = false;

    async function loadSearchSeo() {
      try {
        const { response, data } = await fetchWrapper.get(`${config.apis.api.url}/seo/search/artlists`);
        if (ignore || !response?.ok || !data || typeof data !== 'object') {
          return;
        }

        setSearchSeo(data);
      } catch (error) {
        if (!ignore) {
          console.error('Failed to load artlists SEO payload', error);
        }
      }
    }

    loadSearchSeo();

    return () => {
      ignore = true;
    };
  }, []);

  return (
    <>
      <StaticSeoHead
        title={searchSeo?.title || "Curated Artlists | Suarte"}
        description={searchSeo?.metaDescription || "Explore curated artlists on Suarte. Press play to enjoy full-screen selections of original artworks from artists, galleries, and the art community."}
        canonicalUrl={searchSeo?.canonicalUrl}
        canonicalPath={searchSeo?.canonicalPath || "/artlists"}
        robots={searchSeo?.robots}
        ogType={searchSeo?.ogType}
        image={searchSeo?.ogImage}
        structuredData={searchSeo?.structuredData}
        pageType="CollectionPage"
        breadcrumbItems={[
          { name: 'Home', url: 'https://suarte.art/' },
          { name: 'Artlists', url: 'https://suarte.art/artlists' },
        ]}
      />
      <AppNavigationPage>
        <RandomizedHomeLinksList
          className="home-artlists__page home-artlists__randomized"
          randomizedState={randomizedState}
          emptyText="No artlists available at the moment."
          emptyTextClassName="home-artlists__empty-text font-size-medium"
          VirtualListComponent={VirtualList}
          virtualListProps={{ showSearchCTA: true }}
        >
          <HomeHeader
            onInput={() => {}}
            value=""
            icon={<ArtlistIcon className="home-artlists__header-icon" />}
            text={tProfileTabs('artlists')}
            placeholder="Search for artlists..."
            hideSearch
          />
        </RandomizedHomeLinksList>
      </AppNavigationPage>
    </>
  );
}

function HomeArtlistsSearch({ valueFromSearch = "", fromSearch = false }) {
  const searchValue = typeof valueFromSearch?.q === "string" ? valueFromSearch.q.trim() : "";
  const query = useMemo(() => ({ q: searchValue }), [searchValue]);

  const { loading, queryData, loadMoreData } = useQuery(
    "home_artlists",
    `${config.apis.api.url}/search/artlist`,
    query,
    {
      invalidateWhen: ["NEW_ARTLIST", "EDIT_ARTLIST", "DELETE_ARTLIST"],
      optionalToken: true,
    }
  );

  return (
    <div className="home-artlists__page">
      {loading ? (
        <Loader/>
      ) : queryData.data.data.length === 0 ? (
        <Text className="home-artlists__empty-text font-size-medium" paragraph>
          {searchValue ? "No artlists available for your search query." : "No artlists available at the moment."}
        </Text>
      ) : (
        <VirtualList 
          key={queryData.queryString} 
          items={queryData.data} 
          onLoadMore={loadMoreData}
          fromSearch={fromSearch}
        />
      )}
    </div>
  );
}

export default function HomeArtlists(props) {
  if (props.fromSearch) {
    return <HomeArtlistsSearch {...props} />;
  }
  return <HomeArtlistsRandomized />;
}
